<template>
  <div class="register">
    <div class="register__container">
      <div class="register__brand">
        <img class="tingg-brand-logo" src="https://developer.tingg.africa/checkout/v2/assets/images/tingg/tingg_white.png" height="64" alt="Tingg"/>
      </div>

      <div class="register__card row no-gutters">
        <div class="col-md-6">
          <ul class="wizard-vertical">
            <li class="wizard-vertical__step active">
              <span class="wizard-vertical__step__count">1</span> Profile Information
            </li>
            <li class="wizard-vertical__divider"></li>
            <li class="wizard-vertical__step active">
              <span class="wizard-vertical__step__count">2</span> Business Information
            </li>
            <li class="wizard-vertical__divider"></li>
            <li class="wizard-vertical__step">
              <span class="wizard-vertical__step__count">3</span> Complete
            </li>
          </ul>
        </div>

        <div class="col-md-6 register__form-section">
          <div id="login-error" v-show="showError" class="help-text help-text--error mb-4" style="display: none;" v-html="error"></div>
          <form class="mb-5" @submit.prevent="createAccount">
            <div :class="{'form-group': true, 'has-error': errors.has('business_name') }">
              <label class="mula-form-group__label control-label" for="business_name">Business name</label>
              <input v-validate="'required|min:3'"
                     class="mula-form-group__input form-control"
                     id="business_name"
                     name="business_name"
                     type="text"
                     autocomplete="business_name"
                     v-model="business_name"
              >
              <span v-show="errors.has('business_name')" class="help is-danger">
                        {{ errors.first('business_name') }}
                      </span>
            </div>

            <div :class="{'form-group': true, 'has-error': errors.has('country_of_operation') }">
              <label class="mula-form-group__label control-label" for="country_of_operation">
                Country of operation
              </label>
              <select v-validate="'required'" class="mula-form-group__input form-control" name="country_of_operation" id="country_of_operation" v-model="country_of_operation">
                <option v-for="(country, index) in countries" :value="country.code" v-text="country.name" :key="`#${index}${country.code}`"></option>
              </select>
              <span v-show="errors.has('country_of_operation')" class="help is-danger">
                {{ errors.first('country_of_operation') }}
              </span>
            </div>

            <div :class="{'form-group': true, 'has-error': errors.has('phone_number') }">
              <label class="mula-form-group__label control-label" for="phone_number">Phone number</label>
              <span class="help muted subtle-text mt-0 mb-2">Please provide a mobile number with the country code</span>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">+</span>
                </div>
                <input v-validate="'required|phone_number'" class="mula-form-group__input form-control" id="phone_number" name="phone_number" type="text" autocomplete="phone_number" v-model="phone_number">
              </div>
              <span v-show="errors.has('phone_number')" class="help is-danger">
                {{ errors.first('phone_number') }}
              </span>
            </div>

            <div class="register__navigation">
              <button class="ml-btn ml-btn--md tingg-primary-button" type="submit">Finish</button>

              <button class="ml-btn ml-btn--md ml-btn-white" @click.prevent="back()">
                <i class="fas fa-chevron-left"></i>
                <span>Back</span>
              </button>
            </div>
          </form>
          <div class="register__secondary-cta">
            <span>Already have an account?</span>
            <a class="tingg-anchors" href="#" @click.prevent="goTo('/login')">Login</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Config from '@/api/config'
  import User from '@/api/services/user'
  import { Validator } from 'vee-validate'
  import { mapActions, mapGetters } from 'vuex'
  import PhoneNumber from 'awesome-phonenumber'

  const logInfo = !!(process.env.NODE_ENV !== 'production')

  export default {
    computed: {
      ...mapGetters('registration', ['businessInformation', 'information']),
      base_currency () {
        return Config.CURRENCIES[this.country_of_operation]
      },
      countries () {
        return Config.COUNTRIES
      }
    },

    data () {
      return {
        business_name: '',
        country_of_operation: '',
        errorStatCode: 7,
        phone_number: '',
        showError: false
      }
    },

    methods: {
      ...mapActions('registration', ['completeRegistration', 'persist']),
      attachCustomValidators () {
        Validator.extend('phone_number', {
          getMessage: field => `The phone number provided is not a valid phone number.`,
          validate: value => new Promise((resolve) => {
            let isPhoneNumberValid = PhoneNumber(`+${value}`).isValid()
            resolve({
              valid: isPhoneNumberValid
            })
          })
        })
      },
      back () {
        this.persist({
          clientName: this.business_name,
          defaultCurrencyCode: (this.base_currency !== undefined && this.base_currency !== null) ? this.base_currency.code : undefined,
          businessCountryCode: this.country_of_operation,
          mobileNumber: this.phone_number
        })

        this.$router.push('/register/user')
      },
      createAccount () {
        this.hideErrorMsg()
        this.$btn.disable('button[type="submit"]')

        this.$validator.validateAll()
          .then(result => {
            logInfo && console.log('Did the user information pass validation?', result)

            if (result) {
              this.persist({
                clientName: this.business_name,
                defaultCurrencyCode: (this.base_currency !== undefined && this.base_currency !== null) ? this.base_currency.code : undefined,
                businessCountryCode: this.country_of_operation,
                mobileNumber: this.phone_number
              })
              User.create(this.information)
                .then(response => {
                  logInfo && console.log('Was the account created successfully?', response.SUCCESS)
                  this.completeRegistration()
                  this.$router.push('/register/complete')
                })
                .catch(reason => {
                  logInfo && console.log('Creating an account failed: ', reason)
                  this.showErrorMsg(reason)
                  this.$btn.enable('button[type="submit"]')
                })
            } else {
              this.$btn.enable('button[type="submit"]')
            }
          })
          .catch(reason => {
            logInfo && console.log('Failed to validate the user information during registration', reason)
          })
      }
    },
    mounted () {
      let businessInfo = this.businessInformation

      logInfo && console.log('Business information collected so far', businessInfo)

      // Populate the data fields
      if (businessInfo != null) {
        this.business_name = businessInfo.clientName
        this.country_of_operation = businessInfo.businessCountryCode
        this.phone_number = businessInfo.mobileNumber
      }

      this.attachCustomValidators()
    },
    name: 'RegisterUser'
  }
</script>

<style lang="scss">
  @import "../styles/theme";

  .tingg-anchors {
    color: $tingg-purple !important;
  }

  .register {
    width: 100%;
    min-height: 100vh;

    align-items: start;
    display: flex;
    justify-content: center;
    background-image: linear-gradient(to bottom right, $tingg-primary-red, $tingg-purple);
  }

  .register__container {
    @include make-col-ready();

    @include media-breakpoint-down(xl) {
      @include make-col(8);
    }

    @include media-breakpoint-down(md) {
      @include make-col(10);
    }

    @include media-breakpoint-down(sm) {
      @include make-col(10);
    }

    @include media-breakpoint-down(xs) {
      @include make-col(12);
    }

    height: 75%;

    margin-top: 2.5rem;
  }

  .register__card {
    background-color: $background-white;

    min-height: 100%;
    width: 100%;

    padding: 1.5rem 3rem;
  }

  .register__card-title {
    width: 100%;

    text-align: center;

    margin-bottom: 1.5rem;
  }

  .register__brand {
    width: 100%;

    align-items: center;
    display: flex;
    justify-content: center;
    margin-bottom: 2.5rem;
  }

  .register__navigation {
    margin: 2rem 0;

    align-items: center;
    display: flex;
    flex-flow: row-reverse;
    justify-content: space-between;
  }
</style>



// WEBPACK FOOTER //
// src/views/RegisterBusiness.vue